import { SyncOperationText } from "@/components/sync/sync-operation-text";
import { UNTITLED_NOTE_TITLE } from "@/domains/untitled/untitled";
import { CustomEventName, dispatchCustomEvent } from "@/modules/custom-event";
import { GuestAppStore } from "@/store";
import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import { BaseSyncOperationGuestMode } from "@/store/sync/operations/BaseSyncOperationGuestMode";
import { SyncErrorHandlingType } from "@/store/sync/operations/errors/SyncError";
import {
  IGrantNoteAclViaEmailAddressOperationGuestMode,
  IGrantNoteAclViaEmailAddressOperation,
  GuestModeSupportedSyncOperationKind,
} from "@/store/sync/operations/types";
import { SyncCustomErrorData, SYNC_OPERATION_EMAIL_NOT_FOUND_EXCEPTION } from "@/store/sync/types";

export class GrantNoteAclViaEmailAddressOperation extends BaseSyncOperation<IGrantNoteAclViaEmailAddressOperation> {
  get operationKind(): GuestModeSupportedSyncOperationKind {
    return "GRANT_NOTE_ACL_VIA_EMAIL_ADDRESS";
  }

  get successToastMessage() {
    return (
      <>
        Sharing {this.mediumTitle} with {this.mediumEmail}...
      </>
    );
  }

  get title() {
    return (
      (this.store instanceof GuestAppStore
        ? this.store.note.getNoteObservableById({ noteId: this.payload.id })
        : this.store.notes.get(this.payload.id)
      )?.title || UNTITLED_NOTE_TITLE
    );
  }

  get mediumTitle() {
    return <SyncOperationText>{this.title}</SyncOperationText>;
  }

  get mediumEmail() {
    return <SyncOperationText>{this.payload.email_address}</SyncOperationText>;
  }

  handleInvalidError(_errorData: SyncCustomErrorData) {
    if (_errorData.metadata?.error_cls === SYNC_OPERATION_EMAIL_NOT_FOUND_EXCEPTION) {
      dispatchCustomEvent(CustomEventName.ShareFailed, {
        email: this.payload.email_address,
      });

      this.ignoreError();
      return;
    }

    this.triggerToast(
      <>{this.mediumEmail} could not be shared. If this error continues, please contact support.</>
    );
  }

  handlePermissionDeniedError(_errorData: SyncCustomErrorData) {
    this.triggerToast(
      <>You do not have permissions to share {this.mediumEmail}. Please contact the note owner.</>
    );
  }

  handleUnknownError(_errorData: SyncCustomErrorData) {
    this.triggerToast(
      <>{this.mediumEmail} could not be shared. If this error continues, please contact support.</>,
      SyncErrorHandlingType.RetryWithLimit
    );
  }

  async triggerRecompute() {
    await this.store.notes.recompute(this.payload.id);
  }
}

export class GrantNoteAclViaEmailAddressOperationGuestMode extends BaseSyncOperationGuestMode<
  IGrantNoteAclViaEmailAddressOperation,
  IGrantNoteAclViaEmailAddressOperationGuestMode
> {
  triggerRecompute(): Promise<void> {
    return Promise.resolve();
  }

  get operationKind(): GuestModeSupportedSyncOperationKind {
    return "GRANT_NOTE_ACL_VIA_EMAIL_ADDRESS";
  }
}
